/**
 * Shared map utilities for DACSAI and DRLP exploration features.
 * 
 * This module contains reusable:
 * - Marker icons (store types, user location)
 * - Distance calculations
 * - Zoom level calculations
 * - Color constants
 */

import L from 'leaflet';

// ============================================
// COLOR CONSTANTS - DACSAI Standard
// ============================================

export const COLORS = {
  // GREEN = DRLP (DealShaq Partner) inside radius
  DRLP_INSIDE: {
    fill: '#22c55e',      // green-500
    border: '#16a34a',    // green-600
    bg: 'bg-green-500',
    bgLight: 'bg-green-50',
    text: 'text-green-700',
    borderClass: 'border-green-200',
  },
  // YELLOW = DRLP (DealShaq Partner) outside radius
  DRLP_OUTSIDE: {
    fill: '#eab308',      // yellow-500
    border: '#ca8a04',    // yellow-600
    bg: 'bg-yellow-500',
    bgLight: 'bg-yellow-50',
    text: 'text-yellow-700',
    borderClass: 'border-yellow-200',
  },
  // RED = Non-DRLP store (not a DealShaq partner)
  NON_DRLP: {
    fill: '#ef4444',      // red-500
    border: '#dc2626',    // red-600
    bg: 'bg-red-500',
    bgLight: 'bg-red-50',
    text: 'text-red-700',
    borderClass: 'border-red-200',
  },
  // BLUE = User location / center point
  USER_LOCATION: {
    fill: '#3b82f6',      // blue-500
    border: '#1d4ed8',    // blue-700
  },
  // RADIUS circle
  RADIUS: {
    stroke: '#22c55e',    // green-500
    fill: '#22c55e',
    fillOpacity: 0.1,
  }
};

// ============================================
// MARKER ICONS
// ============================================

/**
 * Create a store marker icon based on type.
 * @param {'drlp-inside' | 'drlp-outside' | 'non-drlp'} type - Store type
 * @param {boolean} hasInventory - Whether store has active inventory (optional, for visual differentiation)
 * @returns {L.DivIcon} Leaflet div icon
 */
export function createStoreIcon(type, hasInventory = true) {
  let color, borderColor;
  
  if (type === 'drlp-inside') {
    color = hasInventory ? COLORS.DRLP_INSIDE.fill : '#86efac';  // green-500 or green-300
    borderColor = hasInventory ? COLORS.DRLP_INSIDE.border : COLORS.DRLP_INSIDE.fill;
  } else if (type === 'drlp-outside') {
    color = hasInventory ? COLORS.DRLP_OUTSIDE.fill : '#fde047';  // yellow-500 or yellow-300
    borderColor = hasInventory ? COLORS.DRLP_OUTSIDE.border : COLORS.DRLP_OUTSIDE.fill;
  } else {
    color = COLORS.NON_DRLP.fill;
    borderColor = COLORS.NON_DRLP.border;
  }
  
  return L.divIcon({
    className: `store-marker-${type}`,
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background-color: ${color};
        border: 3px solid ${borderColor};
        border-radius: 50%;
        box-shadow: 0 3px 8px rgba(0,0,0,0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="8" cy="21" r="1"/>
          <circle cx="19" cy="21" r="1"/>
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
}

/**
 * User location marker icon (home/center point).
 */
export const userLocationIcon = L.divIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 36px;
      height: 36px;
      background-color: ${COLORS.USER_LOCATION.fill};
      border: 3px solid ${COLORS.USER_LOCATION.border};
      border-radius: 50%;
      box-shadow: 0 3px 10px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22" fill="${COLORS.USER_LOCATION.fill}"/>
      </svg>
    </div>
  `,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

// ============================================
// DISTANCE CALCULATIONS
// ============================================

/**
 * Calculate distance between two coordinates using Haversine formula.
 * @param {{lat: number, lng: number}} coord1 - First coordinate
 * @param {{lat: number, lng: number}} coord2 - Second coordinate
 * @returns {number} Distance in miles
 */
export function calculateDistanceMiles(coord1, coord2) {
  const R = 3959; // Earth's radius in miles
  const lat1 = coord1.lat * Math.PI / 180;
  const lat2 = coord2.lat * Math.PI / 180;
  const dlat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dlng = (coord2.lng - coord1.lng) * Math.PI / 180;
  
  const a = Math.sin(dlat/2) * Math.sin(dlat/2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dlng/2) * Math.sin(dlng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  
  return R * c;
}

/**
 * Calculate point at given distance and bearing from a center point.
 * @param {{lat: number, lng: number}} center - Center coordinate
 * @param {number} distanceMiles - Distance in miles
 * @param {number} bearingDegrees - Bearing in degrees (0 = North, 90 = East)
 * @returns {{lat: number, lng: number}} New coordinate
 */
export function calculatePointAtDistance(center, distanceMiles, bearingDegrees) {
  const R = 3959; // Earth's radius in miles
  const brng = bearingDegrees * Math.PI / 180;
  const d = distanceMiles;
  
  const lat1 = center.lat * Math.PI / 180;
  const lng1 = center.lng * Math.PI / 180;
  
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d / R) +
    Math.cos(lat1) * Math.sin(d / R) * Math.cos(brng)
  );
  
  const lng2 = lng1 + Math.atan2(
    Math.sin(brng) * Math.sin(d / R) * Math.cos(lat1),
    Math.cos(d / R) - Math.sin(lat1) * Math.sin(lat2)
  );
  
  return {
    lat: lat2 * 180 / Math.PI,
    lng: lng2 * 180 / Math.PI
  };
}

// ============================================
// ZOOM CALCULATIONS
// ============================================

/**
 * Calculate appropriate zoom level based on radius.
 * @param {number} radiusMiles - Radius in miles
 * @returns {number} Zoom level (10-15)
 */
export function getZoomLevel(radiusMiles) {
  if (radiusMiles <= 0.5) return 15;
  if (radiusMiles <= 1) return 14;
  if (radiusMiles <= 2) return 13;
  if (radiusMiles <= 4) return 12;
  if (radiusMiles <= 6) return 11;
  return 10;
}

// ============================================
// STORE CATEGORIZATION
// ============================================

/**
 * Categorize stores based on distance from center and DRLP status.
 * @param {Array} stores - Array of store objects
 * @param {{lat: number, lng: number}} center - Center coordinate
 * @param {number} radiusMiles - Radius in miles
 * @returns {Array} Categorized stores with markerType and distance
 */
export function categorizeStores(stores, center, radiusMiles) {
  return stores.map(store => {
    const coords = store.coordinates || store.location;
    if (!coords || (coords.lat === 0 && coords.lng === 0)) return null;
    
    const distance = center?.lat 
      ? calculateDistanceMiles(center, coords)
      : Infinity;
    
    const isInsideRadius = distance <= radiusMiles;
    const isDrlp = store.is_drlp !== false; // Default to true for DRLP searches
    
    // Determine marker type using DACSAI standard:
    // GREEN = DRLP inside radius
    // YELLOW = DRLP outside radius
    // RED = Non-DRLP
    let markerType;
    if (!isDrlp) {
      markerType = 'non-drlp';
    } else if (isInsideRadius) {
      markerType = 'drlp-inside';
    } else {
      markerType = 'drlp-outside';
    }
    
    return {
      ...store,
      coords,
      distance: parseFloat(distance.toFixed(2)),
      isInsideRadius,
      isDrlp,
      markerType
    };
  }).filter(Boolean);
}

/**
 * Sort stores by marker type priority and distance.
 * Priority: DRLP inside > DRLP outside > Non-DRLP, then by distance
 * @param {Array} stores - Array of categorized stores
 * @returns {Array} Sorted stores
 */
export function sortStoresByPriority(stores) {
  const order = { 'drlp-inside': 0, 'drlp-outside': 1, 'non-drlp': 2 };
  return [...stores].sort((a, b) => {
    if (order[a.markerType] !== order[b.markerType]) {
      return order[a.markerType] - order[b.markerType];
    }
    return a.distance - b.distance;
  });
}

// ============================================
// STORE TYPE BADGES
// ============================================

/**
 * Get badge styling for store type.
 * @param {string} type - Store type (Grocery, Supermarket, etc.)
 * @returns {{bg: string, text: string}} Tailwind CSS classes
 */
export function getStoreTypeBadge(type) {
  const typeMap = {
    'Grocery': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    'Grocery Store': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    'Supermarket': { bg: 'bg-blue-100', text: 'text-blue-700' },
    'Specialty': { bg: 'bg-purple-100', text: 'text-purple-700' },
    'Convenience': { bg: 'bg-orange-100', text: 'text-orange-700' },
    'Organic': { bg: 'bg-green-100', text: 'text-green-700' },
    'Natural Foods': { bg: 'bg-lime-100', text: 'text-lime-700' },
  };
  return typeMap[type] || { bg: 'bg-gray-100', text: 'text-gray-700' };
}
